/**
 * DSK-695: after a Desktop WebView reload, Rust keeps recording + uploading
 * chunks (DSK-334) but every FE recording atom (timer, activeSessionIdAtom,
 * activeRecordingTransportAtom, transport + capture gates) resets. This
 * restores FE recording ownership from the native recorder so the UI matches
 * Rust, mirroring Chronicle's
 * `recording_recovered` + `seedRecoveredTimer` for the online desktop-mic path.
 *
 * Extends (replaces) `useReattachOnlineChunkListenersOnBoot`: UI recovery runs
 * first, then the existing upload-listener re-attach + fresh token push.
 */
import { activeRecordingTransportAtom } from '@/atoms/chronicle';
import { activeSessionIdAtom } from '@/atoms/liveTranscriptionAtom';
import { resolveIsAppsRustAuth0TokenRefreshDesktopEnabled } from '@/features/flags/rust-auth0-token-refresh-desktop';
import { seedRecoveredTimer } from '@/providers/chronicle-state-provider/utils/process-recording-recovered';
import { store } from '@/store';
import { recorderFmc } from '@/tauri/controller/recording';
import {
  attachOnlineChunkUploadListeners,
  pushFreshOnlineChunkToken,
  setOnlineChunkUploadIntl,
} from '@/tauri/online-chunk-upload/listeners';
import {
  onlineChunkUploadBridge,
  type ActiveOnlineChunkUpload,
} from '@/tauri/online-chunk-upload/session';
import { classifyRecorderLiveness } from '@/tauri/recorder-liveness';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';
import { ensureQuerySessionById } from '@heidi/session/data';
import { type QueryClient } from '@tanstack/react-query';
import { getDefaultStore } from 'jotai';
import { useEffect } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { type RecordingLiveness } from 'src-tauri/src/bindings/RecordingLiveness';

/** Outcome of a boot recovery attempt — for logging/tests only. */
export type RecoverDesktopRecordingOutcome =
  | {
      status: 'skipped';
      reason:
        | 'not_tauri'
        | 'no_active_upload'
        | 'missing_session_id'
        | 'recorder_stopped'
        | 'recorder_zombie'
        | 'no_upload_session'
        | 'liveness_probe_failed'
        | 'session_mismatch'
        | 'cancelled';
    }
  | {
      status: 'recovered';
      sessionId: string;
      /** Rust's elapsed wall-clock for the CURRENT segment. */
      elapsedSecs: number;
      /** What the timer was actually seeded to (segment elapsed floored by the
       *  known session duration — see the multi-segment note below). */
      seededSecs: number;
      isPaused: boolean;
    };

export interface RecoverDesktopRecordingUiStateInput {
  /**
   * Rust's upload-session snapshot, or `null` when the probe itself failed.
   * `null` is NOT the same as `{ active: false }`: an inactive session is a
   * fact about the pipeline, a failed probe is an absence of information, and
   * only the former may veto recovery (see the unarmed-recorder guard below).
   */
  activeUpload: ActiveOnlineChunkUpload | null;
  liveness: RecordingLiveness;
}

export interface RecoverDesktopRecordingDeps {
  store: typeof store;
  jotaiStore: ReturnType<typeof getDefaultStore>;
  queryClient: QueryClient;
}

/** Whole seconds of a session `duration`, 0 for missing / non-positive values. */
function durationSecs(duration: number | null | undefined): number {
  return typeof duration === 'number' && Number.isFinite(duration) && duration > 0
    ? Math.floor(duration)
    : 0;
}

/** Persisted session duration (seconds) from the query cache, 0 when absent. */
function cachedSessionDurationSecs(queryClient: QueryClient, sessionId: string): number {
  return durationSecs(
    queryClient.getQueryData<QuerySessionResponse>([GET_SESSION_BY_ID, { sessionId }])
      ?.duration
  );
}

/**
 * Advance a stale IDLE session-cache entry so the mini player + session-page
 * controls derive from it without waiting on a refetch. Only touches an IDLE
 * entry — never downgrades a status a concurrent update already advanced, and
 * never creates one (an updater returning `undefined` writes nothing).
 */
function patchRecoveredSessionStatus(
  queryClient: QueryClient,
  sessionId: string,
  isPaused: boolean
): void {
  queryClient.setQueryData<QuerySessionResponse>(
    [GET_SESSION_BY_ID, { sessionId }],
    (old) => {
      if (!old || old.recording_status !== RecordingStatus.IDLE) return old;
      return {
        ...old,
        recording_status: isPaused ? RecordingStatus.PAUSED : RecordingStatus.RECORDING,
      };
    }
  );
}

/**
 * Decide whether recovery should run and apply FE state writes. Pure guards +
 * side effects via injected deps, so the guard logic is testable without
 * mounting React or a real Tauri runtime.
 */
export function recoverDesktopRecordingUiState(
  input: RecoverDesktopRecordingUiStateInput,
  deps: RecoverDesktopRecordingDeps
): RecoverDesktopRecordingOutcome {
  const { activeUpload, liveness } = input;

  if (liveness.state === 'Stopped') {
    return { status: 'skipped', reason: 'recorder_stopped' };
  }
  if (!liveness.session_id) {
    // Logged (unlike `recorder_stopped`, which is simply what every ordinary
    // boot looks like): a non-Stopped recorder with no session id is a state
    // no start path produces, and it was previously invisible in Datadog.
    logger.warn('DESKTOP_RECORDING_UI_RECOVERY_MISSING_SESSION_ID', {
      recorderState: liveness.state,
      elapsedSecs: liveness.elapsed_secs,
    });
    return { status: 'skipped', reason: 'missing_session_id' };
  }
  // `state` alone can't tell a live recording from a zombie — a recorder stuck
  // at `Recording` with a dead PCM cursor reports the same thing. Recovering
  // one would show a ticking timer over nothing being captured AND stamp
  // `activeRecordingTransportAtom`, which makes the start path treat it as a
  // live owned recording and skip the convergence that resolves it (APP-6140).
  // Leave the UI un-recovered instead, so a Record click still takes over.
  const zombie = classifyRecorderLiveness(liveness);
  if (zombie.isZombie) {
    logger.warn('DESKTOP_RECORDING_UI_RECOVERY_ZOMBIE_RECORDER', {
      sessionId: liveness.session_id,
      recorderState: liveness.state,
      elapsedSecs: liveness.elapsed_secs,
      secsSinceLastData: liveness.secs_since_last_data,
      zombieReason: zombie.reason,
    });
    return { status: 'skipped', reason: 'recorder_zombie' };
  }
  // A `Recording` recorder with no active Rust upload session is capturing with
  // nowhere to send the audio. Recovering it would stamp
  // `activeRecordingTransportAtom`, which the start path reads back as
  // `isLiveOwnedRecording` and uses to skip the convergence that resolves this
  // state — so a Record click could no longer take the capture over. Leave it
  // un-recovered, as for a zombie above; the start path then stops the recorder
  // and rebuilds the whole capture contract from scratch.
  //
  // Narrowed three ways, each of which would otherwise veto a healthy recovery:
  //  - `Recording` only, the one state where an absent session means audio is
  //    being captured and dropped.
  //  - `use_chunk_streaming` only. The legacy uploader holds no native session,
  //    so `active: false` says nothing about it either way.
  //  - a known snapshot only. `null` means the probe failed, not that the
  //    session is absent.
  if (
    liveness.state === 'Recording' &&
    liveness.use_chunk_streaming &&
    activeUpload !== null &&
    !activeUpload.active
  ) {
    // Named for what was observed, not for why: a never-armed pipeline, a
    // device-change teardown whose restart never landed, and an upload session
    // stopped under a still-live recorder are indistinguishable from here.
    logger.warn('DESKTOP_RECORDING_UI_RECOVERY_NO_UPLOAD_SESSION', {
      sessionId: liveness.session_id,
      elapsedSecs: liveness.elapsed_secs,
      secsSinceLastData: liveness.secs_since_last_data,
    });
    return { status: 'skipped', reason: 'no_upload_session' };
  }
  // Require agreement between the two probes before trusting either — an
  // upload session bound to a DIFFERENT recording than the live recorder
  // would otherwise restore ownership of the wrong session. Prefer the
  // upload's session id in the log: it is what a subsequent Resume click
  // would route stop/start against.
  if (
    activeUpload?.active &&
    (!activeUpload.sessionId || liveness.session_id !== activeUpload.sessionId)
  ) {
    logger.warn('DESKTOP_RECORDING_UI_RECOVERY_SESSION_MISMATCH', {
      uploadSessionId: activeUpload.sessionId,
      livenessSessionId: liveness.session_id,
    });
    return { status: 'skipped', reason: 'session_mismatch' };
  }

  const sessionId = liveness.session_id;
  const useRustChunkUpload = liveness.use_chunk_streaming;
  const isPaused = liveness.state === 'Paused';
  const elapsedSecs = Math.floor(liveness.elapsed_secs);
  // Rust's `elapsed_secs` is `now - starting_time`, and `starting_time` resets
  // on every `recorderFmc.start()` — i.e. on each pause→resume and each
  // mid-recording device change. The FE timer instead accumulates across
  // segments (APP-6013), so seeding the raw segment elapsed alone would
  // rewind a 40-minute paused-once consult to its current 3-minute segment.
  // `session.duration` only persists prior COMPLETED segments (APP-7623) —
  // it never includes the live one — so the recovered total is the sum, not
  // the max; `reconcileRecoveredSessionState` raises the floor again once the
  // (usually cold-after-reload) cache entry is fetched.
  const seededSecs = elapsedSecs + cachedSessionDurationSecs(deps.queryClient, sessionId);

  seedRecoveredTimer(seededSecs, isPaused);
  deps.jotaiStore.set(activeSessionIdAtom, sessionId);
  deps.jotaiStore.set(activeRecordingTransportAtom, 'desktop-mic');
  deps.store.dispatch.tauriRecordModel.setUseRustChunkUpload(useRustChunkUpload);
  deps.store.dispatch.tauriRecordModel.setLowDiskMonitorEnabled(
    liveness.low_disk_monitor_enabled
  );
  patchRecoveredSessionStatus(deps.queryClient, sessionId, isPaused);

  logger.info('DESKTOP_RECORDING_UI_RECOVERED', {
    sessionId,
    elapsedSecs,
    seededSecs,
    isPaused,
    useRustChunkUpload,
    lowDiskMonitorEnabled: liveness.low_disk_monitor_enabled,
  });

  return { status: 'recovered', sessionId, elapsedSecs, seededSecs, isPaused };
}

/**
 * Second pass over the writes that need the session itself. After a reload the
 * `[GET_SESSION_BY_ID]` entry is cold — session queries are not persisted, and
 * this runs at the app root before the session page mounts its query — so the
 * synchronous pass above has no duration floor to apply and no entry to patch.
 * Fetching through `ensureQuerySessionById` populates the entry the page will
 * read (a cache hit, not an extra fetch, once it mounts), which makes both
 * writes land.
 */
export async function reconcileRecoveredSessionState(
  recovered: {
    sessionId: string;
    elapsedSecs: number;
    seededSecs: number;
    isPaused: boolean;
  },
  deps: Pick<RecoverDesktopRecordingDeps, 'jotaiStore' | 'queryClient'> & {
    ensureSession: typeof ensureQuerySessionById;
    signal?: AbortSignal;
  }
): Promise<void> {
  const { sessionId, elapsedSecs, seededSecs, isPaused } = recovered;
  try {
    const session = await deps.ensureSession(sessionId);
    if (deps.signal?.aborted) return;
    // A stop (or a start on another session) landing during the fetch means
    // this recovery no longer owns the UI — its writes would resurrect it.
    if (deps.jotaiStore.get(activeSessionIdAtom) !== sessionId) return;

    patchRecoveredSessionStatus(deps.queryClient, sessionId, isPaused);

    // Same sum as the synchronous pass (see `recoverDesktopRecordingUiState`)
    // — `session.duration` never includes the live segment, so the raised
    // total must add the segment elapsed back in, not replace it.
    const raisedSecs = durationSecs(session?.duration) + elapsedSecs;
    if (raisedSecs > seededSecs) {
      seedRecoveredTimer(raisedSecs, isPaused);
      logger.info('DESKTOP_RECORDING_UI_RECOVERY_DURATION_RAISED', {
        sessionId,
        seededSecs,
        raisedSecs,
      });
    }
  } catch (error) {
    // Best effort: the timer keeps the segment-elapsed seed and the page's own
    // query still derives the status a moment later.
    logger.warn('DESKTOP_RECORDING_UI_RECOVERY_SESSION_FETCH_FAILED', {
      sessionId,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Full boot sequence: read upload state plus the recorder's authoritative
 * liveness/capture snapshot, recover FE UI state for either upload transport,
 * then re-attach upload listeners only when Rust owns upload. Called once on
 * mount.
 */
export async function recoverDesktopRecordingAfterReload(options?: {
  signal?: AbortSignal;
  intl?: IntlShape;
}): Promise<RecoverDesktopRecordingOutcome> {
  if (options?.intl) {
    setOnlineChunkUploadIntl(options.intl);
  }

  if (!isTauri()) {
    return { status: 'skipped', reason: 'not_tauri' };
  }

  // Never overwritten by a failure below: a recovery that succeeded must stay
  // reported as recovered even if the re-attach afterwards throws, or the
  // DSK-695 Datadog signal under-counts recoveries and mislabels the failures
  // as "no active upload".
  let outcome: RecoverDesktopRecordingOutcome = {
    status: 'skipped',
    reason: 'no_active_upload',
  };

  // `null` until the probe answers. A failed probe must NOT read as "no upload
  // session": the guard below treats an inactive session as evidence the audio
  // has nowhere to go, and a transient IPC failure is not that evidence.
  let activeUpload: ActiveOnlineChunkUpload | null = null;
  try {
    activeUpload = await onlineChunkUploadBridge.getActive();
  } catch (error) {
    // Upload state is needed only for listener reattachment. The recorder's
    // native snapshot remains authoritative for capture ownership + gates.
    logger.warn('[online_chunk] boot re-attach skipped', error);
  }
  if (options?.signal?.aborted) return { status: 'skipped', reason: 'cancelled' };

  try {
    const liveness = await recorderFmc.get_recording_liveness();
    outcome = recoverDesktopRecordingUiState(
      { activeUpload, liveness },
      { store, jotaiStore: getDefaultStore(), queryClient: reactQueryClient }
    );
  } catch (livenessError) {
    outcome = { status: 'skipped', reason: 'liveness_probe_failed' };
    logger.warn('DESKTOP_RECORDING_UI_RECOVERY_LIVENESS_PROBE_FAILED', {
      sessionId: activeUpload?.sessionId ?? null,
      error:
        livenessError instanceof Error ? livenessError.message : String(livenessError),
    });
  }
  if (options?.signal?.aborted) return outcome;

  try {
    if (options?.signal?.aborted) return outcome;

    if (activeUpload?.active && activeUpload.sessionId) {
      logger.info('[online_chunk] re-attaching listeners after boot/reload', {
        sessionId: activeUpload.sessionId,
      });
      // Resolve rather than read because this runs on the app root's first
      // commit, before the flag provider is READY — a plain read would lock
      // `false` for a session Rust started with Rust-owned refresh ON.
      const rustAuth0TokenRefresh =
        await resolveIsAppsRustAuth0TokenRefreshDesktopEnabled();
      if (options?.signal?.aborted) return outcome;
      await attachOnlineChunkUploadListeners(
        activeUpload.sessionId,
        rustAuth0TokenRefresh
      );
      if (options?.signal?.aborted) return outcome;
      await pushFreshOnlineChunkToken();
    }

    if (outcome.status === 'recovered') {
      await reconcileRecoveredSessionState(outcome, {
        jotaiStore: getDefaultStore(),
        queryClient: reactQueryClient,
        ensureSession: ensureQuerySessionById,
        signal: options?.signal,
      });
    }

    return outcome;
  } catch (error) {
    logger.warn('[online_chunk] boot re-attach skipped', error);
    return outcome;
  }
}

/**
 * React hook wrapper — mounted once at the app root
 * (`DesktopRecordingRecoveryMount`). Replaces
 * `useReattachOnlineChunkListenersOnBoot`.
 */
export function useRecoverDesktopRecordingAfterReload(): void {
  const intl = useIntl();
  // Kept as its own effect (pre-existing behaviour): a locale change after
  // boot still needs the online-chunk listeners' toast copy updated, without
  // re-running the recovery probe itself.
  useEffect(() => {
    setOnlineChunkUploadIntl(intl);
  }, [intl]);

  useEffect(() => {
    if (!isTauri()) return;

    const controller = new AbortController();
    void recoverDesktopRecordingAfterReload({ signal: controller.signal, intl });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- boot-only: re-running on an intl change would re-probe Rust and could double-recover. The APP-8801 flag is deliberately NOT a dep: it is resolved (readiness-aware) inside the recovery instead of captured from a render, so a mid-boot flag flip can't require a re-run.
  }, []);
}
